<template>
  <div class="oldgold-page-wrapper">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <!-- Executive Toast Notification -->
    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Executive Action Header Card -->
    <v-card flat class="header-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 16px;">
        <!-- Left: Icon & Title -->
        <div class="d-flex align-center">
          <div class="header-icon-box mr-3">
            <v-icon color="#9a6b27" size="24">mdi-scale-balance</v-icon>
          </div>
          <div>
            <div class="d-flex align-center flex-wrap" style="gap: 8px;">
              <span class="page-title">OLD GOLD HISTORY &amp; INWARD REGISTER</span>
              <span class="badge-gold-count">
                {{ filteredDataList.length }} Entries
              </span>
            </div>
            <div class="page-subtitle">
              Customer old gold intake history, purity melt testing, valuation &amp; invoice deductions
            </div>
          </div>
        </div>

        <!-- Right: Actions (Refresh, Export, Print Summary) -->
        <div class="d-flex flex-wrap align-center" style="gap: 10px;">
          <v-btn
            icon
            small
            title="Refresh Records"
            class="refresh-icon-btn"
            :loading="appLoading"
            @click="getData"
          >
            <v-icon small color="#64748b">mdi-refresh</v-icon>
          </v-btn>

          <v-btn
            outlined
            color="#475569"
            class="btn-secondary text-capitalize"
            @click="exportToCSV"
            :disabled="filteredDataList.length === 0"
          >
            <v-icon left small color="#475569">mdi-file-delimited-outline</v-icon>
            Export CSV
          </v-btn>

          <v-btn
            outlined
            color="#9a6b27"
            class="btn-gold-outline text-capitalize"
            @click="printSummaryReport"
            :disabled="filteredDataList.length === 0"
          >
            <v-icon left small color="#9a6b27">mdi-printer-outline</v-icon>
            Print Register
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- 4 KPI Overview Metric Cards -->
    <div class="kpi-grid mb-4">
      <!-- 1. Total Bills / Transactions -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL INTAKE BILLS</div>
          <div class="kpi-value">{{ kpiMetrics.totalBills }}</div>
          <div class="kpi-hint">
            <v-icon x-small color="#64748b" class="mr-1">mdi-receipt-text-outline</v-icon>
            <span>{{ kpiMetrics.totalItems }} total items received</span>
          </div>
        </div>
        <div class="kpi-icon-pill" style="background: #eff6ff; color: #2563eb;">
          <v-icon size="22" color="#2563eb">mdi-receipt-text-check-outline</v-icon>
        </div>
      </div>

      <!-- 2. Total Net Weight -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL NET WEIGHT</div>
          <div class="kpi-value gold-val">{{ kpiMetrics.totalNetWeight.toFixed(3) }} <span class="unit-text">gm</span></div>
          <div class="kpi-hint">
            <span>Gross: <b>{{ kpiMetrics.totalGrossWeight.toFixed(3) }}g</b></span>
            <span class="mx-1">&bull;</span>
            <span>Stone: <b>{{ kpiMetrics.totalStoneWeight.toFixed(3) }}g</b></span>
          </div>
        </div>
        <div class="kpi-icon-pill" style="background: #fefce8; color: #b45309;">
          <v-icon size="22" color="#b45309">mdi-scale</v-icon>
        </div>
      </div>

      <!-- 3. Total Valuation Amount -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL VALUATION</div>
          <div class="kpi-value text-emerald">{{ formatCurrency(kpiMetrics.totalValuation) }}</div>
          <div class="kpi-hint">
            <v-icon x-small color="#10b981" class="mr-1">mdi-cash</v-icon>
            <span>Total adjusted against sales</span>
          </div>
        </div>
        <div class="kpi-icon-pill" style="background: #f0fdf4; color: #15803d;">
          <v-icon size="22" color="#15803d">mdi-cash-multiple</v-icon>
        </div>
      </div>

      <!-- 4. Avg Valuation Rate & Melt -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">AVG RATE / PURITY</div>
          <div class="kpi-value">{{ formatCurrency(kpiMetrics.avgRate) }}<span class="unit-text">/gm</span></div>
          <div class="kpi-hint">
            <v-icon x-small color="#9a6b27" class="mr-1">mdi-fire</v-icon>
            <span>Avg Melt: <b>{{ kpiMetrics.avgMelt.toFixed(1) }}%</b></span>
          </div>
        </div>
        <div class="kpi-icon-pill" style="background: #faf5ff; color: #7c3aed;">
          <v-icon size="22" color="#7c3aed">mdi-chart-bell-curve-cumulative</v-icon>
        </div>
      </div>
    </div>

    <!-- Filters & Search Toolbar Card -->
    <v-card flat class="filter-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 12px;">
        <!-- Left: Search Box -->
        <div class="search-input-wrap">
          <v-text-field
            v-model="keyword"
            background-color="#f8fafc"
            color="#9a6b27"
            clearable
            hide-details
            placeholder="Search by Bill No, Customer Name or Mobile..."
            outlined
            dense
            class="custom-search-field"
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>
        </div>

        <!-- Middle: Date Quick Presets -->
        <div class="date-presets-wrap d-flex align-center flex-wrap" style="gap: 6px;">
          <v-btn
            small
            depressed
            :class="['preset-btn', datePreset === 'all' ? 'preset-active' : '']"
            @click="setDatePreset('all')"
          >
            All Time
          </v-btn>
          <v-btn
            small
            depressed
            :class="['preset-btn', datePreset === 'today' ? 'preset-active' : '']"
            @click="setDatePreset('today')"
          >
            Today
          </v-btn>
          <v-btn
            small
            depressed
            :class="['preset-btn', datePreset === 'week' ? 'preset-active' : '']"
            @click="setDatePreset('week')"
          >
            This Week
          </v-btn>
          <v-btn
            small
            depressed
            :class="['preset-btn', datePreset === 'month' ? 'preset-active' : '']"
            @click="setDatePreset('month')"
          >
            This Month
          </v-btn>
          <v-btn
            small
            depressed
            :class="['preset-btn', datePreset === 'custom' ? 'preset-active' : '']"
            @click="datePreset = 'custom'"
          >
            <v-icon x-small left>mdi-calendar-range</v-icon>
            Custom
          </v-btn>
        </div>

        <!-- Custom Date Range Picker (shown when custom is selected) -->
        <div v-if="datePreset === 'custom'" class="d-flex align-center flex-wrap" style="gap: 8px;">
          <v-menu
            v-model="startDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="startDate"
                label="From Date"
                outlined
                dense
                hide-details
                readonly
                v-bind="attrs"
                v-on="on"
                class="date-input-field"
                prepend-inner-icon="mdi-calendar"
              ></v-text-field>
            </template>
            <v-date-picker v-model="startDate" no-title @input="startDateMenu = false" color="#9a6b27"></v-date-picker>
          </v-menu>

          <span class="text-muted" style="font-size: 12px;">to</span>

          <v-menu
            v-model="endDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="endDate"
                label="To Date"
                outlined
                dense
                hide-details
                readonly
                v-bind="attrs"
                v-on="on"
                class="date-input-field"
                prepend-inner-icon="mdi-calendar"
              ></v-text-field>
            </template>
            <v-date-picker v-model="endDate" no-title @input="endDateMenu = false" color="#9a6b27"></v-date-picker>
          </v-menu>
        </div>

        <!-- Reset Button -->
        <v-btn
          v-if="keyword || datePreset !== 'all' || startDate || endDate"
          small
          text
          color="#dc2626"
          class="text-capitalize px-2"
          @click="resetFilters"
        >
          <v-icon x-small left>mdi-filter-off</v-icon>
          Reset
        </v-btn>
      </div>
    </v-card>

    <!-- Main Data Table Card -->
    <v-card flat class="table-card rounded-lg overflow-hidden">
      <!-- Table Header Bar -->
      <div class="table-header-bar px-4 py-3 d-flex align-center justify-space-between">
        <div class="d-flex align-center" style="gap: 8px;">
          <span class="table-section-title">Intake Records</span>
          <span class="table-count-badge">{{ paginatedItems.length }} of {{ filteredDataList.length }}</span>
        </div>
        <div class="d-flex align-center" style="gap: 12px;">
          <span class="text-caption text-muted">Rows per page:</span>
          <v-select
            v-model="itemsPerPage"
            :items="[10, 20, 50, 100]"
            dense
            hide-details
            outlined
            class="rows-select"
          ></v-select>
        </div>
      </div>

      <!-- Vuetify Data Table with Custom Modern Slots -->
      <v-data-table
        :headers="Headers"
        :items="paginatedItems"
        :single-expand="singleExpand"
        :expanded.sync="expanded"
        item-key="rowKey"
        show-expand
        class="pro-oldgold-table elevation-0"
        hide-default-footer
        :loading="appLoading"
      >
        <!-- Bill No Column -->
        <template #item._id.billNo="{ item }">
          <div class="d-flex align-center" style="gap: 6px;">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span
                  class="bill-badge"
                  v-bind="attrs"
                  v-on="on"
                  @click="goToBill(item)"
                >
                  {{ (item._id && item._id.billNo) || 'N/A' }}
                </span>
              </template>
              <span>Click to view sales bill</span>
            </v-tooltip>
          </div>
        </template>

        <!-- Date Column -->
        <template #item._id.create_date="{ item }">
          <div class="date-cell">
            <div class="d-flex align-center" style="gap: 6px;">
              <span class="date-text">{{ formatDate(item._id && item._id.create_date) }}</span>
              <span v-if="isToday(item._id && item._id.create_date)" class="tag-today">Today</span>
            </div>
            <span class="date-sub">{{ formatTime(item._id && item._id.create_date) }}</span>
          </div>
        </template>

        <!-- Customer Column -->
        <template #item._id.customerName="{ item }">
          <div class="d-flex align-center py-1">
            <div class="customer-avatar mr-2">
              {{ getInitials(item._id && item._id.customerName) }}
            </div>
            <div>
              <div class="customer-name">{{ (item._id && item._id.customerName) || 'Walk-in Customer' }}</div>
              <div class="customer-phone" v-if="item._id && item._id.mobileNumber">
                <v-icon x-small color="#94a3b8" class="mr-1">mdi-phone-outline</v-icon>
                <span>{{ item._id.mobileNumber }}</span>
                <v-btn
                  icon
                  x-small
                  title="Copy Contact"
                  class="copy-btn ml-1"
                  @click.stop="copyText(item._id.mobileNumber)"
                >
                  <v-icon style="font-size: 12px;" color="#94a3b8">mdi-content-copy</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </template>

        <!-- Items Count Column -->
        <template #item.itemsCount="{ item }">
          <span class="items-count-badge">
            <v-icon x-small color="#9a6b27" class="mr-1">mdi-necklace</v-icon>
            {{ (item.items && item.items.length) || item.count || 1 }} {{ ((item.items && item.items.length) || 1) > 1 ? 'items' : 'item' }}
          </span>
        </template>

        <!-- Gross Weight Column -->
        <template #item.grossWeight="{ item }">
          <span class="text-slate-700 font-weight-medium">
            {{ calculateTotalGross(item).toFixed(3) }} <span class="unit-sub">g</span>
          </span>
        </template>

        <!-- Stone Weight Column -->
        <template #item.stoneWeight="{ item }">
          <span class="text-slate-500">
            {{ calculateTotalStone(item).toFixed(3) }} <span class="unit-sub">g</span>
          </span>
        </template>

        <!-- Net Weight Column -->
        <template #item._id.oldGoldNetWeight="{ item }">
          <span class="weight-badge">
            <v-icon x-small color="#b45309" class="mr-1">mdi-scale</v-icon>
            {{ Number((item._id && item._id.oldGoldNetWeight) || calculateTotalNet(item)).toFixed(3) }} g
          </span>
        </template>

        <!-- Total Amount Column -->
        <template #item._id.oldGoldAmount="{ item }">
          <div class="amount-cell">
            <span class="amount-val">{{ formatCurrency((item._id && item._id.oldGoldAmount) || calculateTotalAmount(item)) }}</span>
          </div>
        </template>

        <!-- Quick Actions Column -->
        <template #item.actions="{ item }">
          <div class="d-flex align-center justify-end" style="gap: 4px;">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="table-action-btn"
                  v-bind="attrs"
                  v-on="on"
                  @click="openVoucherDialog(item)"
                >
                  <v-icon size="18" color="#64748b">mdi-receipt-outline</v-icon>
                </v-btn>
              </template>
              <span>View / Print Inward Voucher</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="table-action-btn"
                  v-bind="attrs"
                  v-on="on"
                  @click="goToBill(item)"
                >
                  <v-icon size="18" color="#9a6b27">mdi-open-in-new</v-icon>
                </v-btn>
              </template>
              <span>Open Sales Bill</span>
            </v-tooltip>
          </div>
        </template>

        <!-- Expandable Item Breakdown Drawer -->
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="expanded-row-cell pa-0">
            <div class="item-breakdown-wrapper pa-4">
              <!-- Sub-header -->
              <div class="d-flex align-center justify-space-between mb-3 pb-2 border-b">
                <div class="d-flex align-center" style="gap: 8px;">
                  <div class="sub-icon-box">
                    <v-icon size="16" color="#9a6b27">mdi-format-list-bulleted-type</v-icon>
                  </div>
                  <span class="breakdown-title">Itemized Old Gold Intake Breakdown</span>
                  <span class="sub-bill-badge">Bill #{{ (item._id && item._id.billNo) || 'N/A' }}</span>
                </div>
                <div class="d-flex align-center" style="gap: 8px;">
                  <v-btn
                    x-small
                    outlined
                    color="#9a6b27"
                    class="btn-gold-outline text-capitalize"
                    @click="openVoucherDialog(item)"
                  >
                    <v-icon x-small left>mdi-printer-outline</v-icon>
                    Print Voucher Slip
                  </v-btn>
                  <v-btn
                    x-small
                    dark
                    color="#1e293b"
                    class="btn-dark text-capitalize"
                    @click="goToBill(item)"
                  >
                    <v-icon x-small left>mdi-file-document-outline</v-icon>
                    Full Invoice
                  </v-btn>
                </div>
              </div>

              <!-- Nested Table -->
              <div class="nested-table-wrap">
                <table class="nested-table">
                  <thead>
                    <tr>
                      <th class="text-left" style="width: 45px;">#</th>
                      <th class="text-left">Item Description</th>
                      <th class="text-left">Metal / Purity</th>
                      <th class="text-right">Gold Rate (₹/g)</th>
                      <th class="text-right">Gross Wt (g)</th>
                      <th class="text-right">Stone Wt (g)</th>
                      <th class="text-right">Net Wt (g)</th>
                      <th class="text-right">Melt (%)</th>
                      <th class="text-right">Pure Gold Wt (g)</th>
                      <th class="text-right">Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(val, idx) in item.items" :key="idx" class="nested-table-row">
                      <td class="text-muted font-weight-bold">{{ idx + 1 }}</td>
                      <td>
                        <div class="d-flex align-center" style="gap: 6px;">
                          <v-icon x-small color="#9a6b27">{{ getItemIcon(val.itemName) }}</v-icon>
                          <span class="item-name-text">{{ val.itemName || 'Old Gold Scrap' }}</span>
                        </div>
                      </td>
                      <td>
                        <span class="purity-badge">
                          {{ val.metalType || 'Gold' }} {{ val.purity ? (val.purity + 'K') : '' }}
                        </span>
                      </td>
                      <td class="text-right">
                        <span class="rate-text">{{ val.goldRate ? formatCurrency(val.goldRate) : '-' }}</span>
                      </td>
                      <td class="text-right">{{ Number(val.grossWeight || 0).toFixed(3) }}</td>
                      <td class="text-right text-muted">{{ Number(val.stoneWeight || 0).toFixed(3) }}</td>
                      <td class="text-right font-weight-bold text-slate-800">{{ Number(val.netWeight || 0).toFixed(3) }}</td>
                      <td class="text-right">
                        <span class="melt-badge">{{ Number(val.melt || 0).toFixed(1) }}%</span>
                      </td>
                      <td class="text-right text-gold font-weight-medium">
                        {{ calculatePureGold(val.netWeight, val.melt).toFixed(3) }}
                      </td>
                      <td class="text-right font-weight-bold text-emerald">
                        {{ formatCurrency(val.itemAmount || 0) }}
                      </td>
                    </tr>
                  </tbody>
                  <!-- Table Total Footer -->
                  <tfoot>
                    <tr class="nested-table-footer">
                      <td colspan="4" class="text-right font-weight-bold">Summary Totals:</td>
                      <td class="text-right font-weight-bold">{{ calculateTotalGross(item).toFixed(3) }} g</td>
                      <td class="text-right font-weight-bold text-muted">{{ calculateTotalStone(item).toFixed(3) }} g</td>
                      <td class="text-right font-weight-bold text-slate-900">{{ calculateTotalNet(item).toFixed(3) }} g</td>
                      <td class="text-right font-weight-bold">{{ calculateAvgMeltForItem(item).toFixed(1) }}%</td>
                      <td class="text-right font-weight-bold text-gold">{{ calculateTotalPureGold(item).toFixed(3) }} g</td>
                      <td class="text-right font-weight-bold text-emerald">{{ formatCurrency(calculateTotalAmount(item)) }}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </td>
        </template>

        <!-- No Data / Empty State -->
        <template v-slot:no-data>
          <div class="empty-state-wrap text-center py-8">
            <div class="empty-icon-circle mb-3">
              <v-icon size="32" color="#94a3b8">mdi-scale-balance</v-icon>
            </div>
            <div class="empty-title">No Old Gold Records Found</div>
            <div class="empty-subtitle">
              {{ keyword || datePreset !== 'all' ? 'No records match your active filter criteria. Try resetting filters.' : 'No customer old gold intake entries recorded yet.' }}
            </div>
            <v-btn
              v-if="keyword || datePreset !== 'all'"
              small
              depressed
              color="#9a6b27"
              dark
              class="mt-4 text-capitalize rounded-lg"
              @click="resetFilters"
            >
              <v-icon x-small left>mdi-filter-off</v-icon>
              Clear All Filters
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <!-- Custom Pagination Bar -->
      <div class="table-pagination-bar px-4 py-3 d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
        <div class="pagination-info">
          Showing <b>{{ filteredDataList.length > 0 ? paginatedStart + 1 : 0 }}</b> - <b>{{ paginatedEnd }}</b> of <b>{{ filteredDataList.length }}</b> entries
        </div>
        <v-pagination
          v-if="totalPages > 1"
          v-model="currentPage"
          :length="totalPages"
          :total-visible="7"
          color="#9a6b27"
          class="custom-pagination"
        ></v-pagination>
      </div>
    </v-card>

    <!-- Old Gold Inward Voucher Slip Modal -->
    <v-dialog v-model="voucherDialog" max-width="650px" persistent>
      <v-card class="voucher-modal-card rounded-lg pa-4">
        <!-- Dialog Header -->
        <div class="d-flex align-center justify-space-between pb-3 border-b">
          <div class="d-flex align-center" style="gap: 8px;">
            <div class="header-icon-box">
              <v-icon size="20" color="#9a6b27">mdi-receipt-text-outline</v-icon>
            </div>
            <div>
              <div class="voucher-modal-title">OLD GOLD INWARD VOUCHER</div>
              <div class="voucher-modal-sub">Customer intake receipt &amp; testing statement</div>
            </div>
          </div>
          <v-btn icon small @click="voucherDialog = false">
            <v-icon color="#64748b">mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Printable Voucher Body -->
        <div id="printable-voucher" class="voucher-body pa-4 mt-2" v-if="selectedVoucher">
          <div class="text-center mb-3">
            <div class="store-name">JEWELBIZ SMART JEWELLERY</div>
            <div class="store-sub">Old Gold Intake &amp; Exchange Slip</div>
          </div>

          <div class="voucher-meta-grid mb-3">
            <div>
              <span class="meta-label">VOUCHER / BILL NO:</span>
              <span class="meta-val">#{{ (selectedVoucher._id && selectedVoucher._id.billNo) || 'N/A' }}</span>
            </div>
            <div>
              <span class="meta-label">DATE &amp; TIME:</span>
              <span class="meta-val">{{ formatDate(selectedVoucher._id && selectedVoucher._id.create_date) }}</span>
            </div>
            <div>
              <span class="meta-label">CUSTOMER NAME:</span>
              <span class="meta-val">{{ (selectedVoucher._id && selectedVoucher._id.customerName) || 'Walk-in Customer' }}</span>
            </div>
            <div>
              <span class="meta-label">CONTACT NO:</span>
              <span class="meta-val">{{ (selectedVoucher._id && selectedVoucher._id.mobileNumber) || '-' }}</span>
            </div>
          </div>

          <table class="voucher-table mb-3">
            <thead>
              <tr>
                <th class="text-left">#</th>
                <th class="text-left">Item Description</th>
                <th class="text-right">Gross (g)</th>
                <th class="text-right">Stone (g)</th>
                <th class="text-right">Net Wt (g)</th>
                <th class="text-right">Melt %</th>
                <th class="text-right">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(vItem, vIdx) in selectedVoucher.items" :key="vIdx">
                <td>{{ vIdx + 1 }}</td>
                <td>{{ vItem.itemName || 'Old Gold' }}</td>
                <td class="text-right">{{ Number(vItem.grossWeight || 0).toFixed(3) }}</td>
                <td class="text-right">{{ Number(vItem.stoneWeight || 0).toFixed(3) }}</td>
                <td class="text-right font-weight-bold">{{ Number(vItem.netWeight || 0).toFixed(3) }}</td>
                <td class="text-right">{{ Number(vItem.melt || 0).toFixed(1) }}%</td>
                <td class="text-right font-weight-bold">{{ formatCurrency(vItem.itemAmount || 0) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="4" class="text-right font-weight-bold">Grand Total:</td>
                <td class="text-right font-weight-bold">{{ calculateTotalNet(selectedVoucher).toFixed(3) }} g</td>
                <td></td>
                <td class="text-right font-weight-bold text-emerald">{{ formatCurrency(calculateTotalAmount(selectedVoucher)) }}</td>
              </tr>
            </tfoot>
          </table>

          <div class="voucher-terms pa-2 mb-3">
            <b>Declaration:</b> Customer acknowledges that the old gold received has been tested for purity/melt with customer consent. The net valuation amount is adjusted against sales bill <b>#{{ (selectedVoucher._id && selectedVoucher._id.billNo) || 'N/A' }}</b>.
          </div>

          <div class="d-flex justify-space-between mt-6 pt-4 border-t text-caption">
            <div>
              <div class="sig-line mb-1"></div>
              <span>Customer Signature</span>
            </div>
            <div class="text-right">
              <div class="sig-line mb-1"></div>
              <span>Authorized Appraiser / Cashier</span>
            </div>
          </div>
        </div>

        <!-- Dialog Footer Actions -->
        <div class="d-flex align-center justify-end pt-3 border-t" style="gap: 10px;">
          <v-btn text color="#64748b" class="text-capitalize" @click="voucherDialog = false">
            Close
          </v-btn>
          <v-btn
            dark
            color="#9a6b27"
            class="btn-primary text-capitalize elevation-1"
            @click="printVoucherSlip"
          >
            <v-icon small left>mdi-printer</v-icon>
            Print Slip
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "OldGoldList",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,
      
      dataList: [],
      keyword: "",
      juwellaryId: localStorage.getItem("juwellaryId"),
      
      // Expand state
      expanded: [],
      singleExpand: true,

      // Pagination
      currentPage: 1,
      itemsPerPage: 20,

      // Date Filters
      datePreset: "all",
      startDate: null,
      endDate: null,
      startDateMenu: false,
      endDateMenu: false,

      // Voucher Modal
      voucherDialog: false,
      selectedVoucher: null,

      // Table Headers
      Headers: [
        {
          text: "Bill No.",
          align: "start",
          sortable: true,
          value: "_id.billNo",
          width: "110px",
        },
        { text: "Intake Date", value: "_id.create_date", sortable: true, width: "140px" },
        { text: "Customer Details", value: "_id.customerName", sortable: true },
        { text: "Items", value: "itemsCount", sortable: false, width: "100px" },
        { text: "Gross Wt", value: "grossWeight", sortable: true, width: "110px" },
        { text: "Stone Wt", value: "stoneWeight", sortable: true, width: "110px" },
        { text: "Net Wt (gm)", value: "_id.oldGoldNetWeight", sortable: true, width: "130px" },
        { text: "Total Amount (₹)", value: "_id.oldGoldAmount", sortable: true, width: "140px" },
        { text: "Actions", value: "actions", sortable: false, align: "end", width: "100px" },
        { text: "", value: "data-table-expand", width: "40px" },
      ],
    };
  },
  computed: {
    // Process list to add safe rowKey
    enrichedDataList() {
      if (!this.dataList || !Array.isArray(this.dataList)) return [];
      return this.dataList.map((item, index) => {
        const id = (item._id && (item._id._id || item._id.billNo)) || `og-row-${index}`;
        return {
          ...item,
          rowKey: id,
        };
      });
    },

    // Filtered data by keyword & date range
    filteredDataList() {
      let list = this.enrichedDataList;

      // 1. Keyword filter (Bill No, Customer Name, Mobile Number)
      if (this.keyword && this.keyword.trim() !== "") {
        const q = this.keyword.toLowerCase().trim();
        list = list.filter((item) => {
          const bill = (item._id && item._id.billNo ? String(item._id.billNo) : "").toLowerCase();
          const name = (item._id && item._id.customerName ? String(item._id.customerName) : "").toLowerCase();
          const phone = (item._id && item._id.mobileNumber ? String(item._id.mobileNumber) : "").toLowerCase();
          const itemNames = (item.items || []).map(i => (i.itemName || "").toLowerCase()).join(" ");
          return bill.includes(q) || name.includes(q) || phone.includes(q) || itemNames.includes(q);
        });
      }

      // 2. Date Filter
      if (this.datePreset !== "all") {
        const now = new Date();
        list = list.filter((item) => {
          if (!item._id || !item._id.create_date) return true;
          const itemDate = new Date(item._id.create_date);
          
          if (this.datePreset === "today") {
            return (
              itemDate.getDate() === now.getDate() &&
              itemDate.getMonth() === now.getMonth() &&
              itemDate.getFullYear() === now.getFullYear()
            );
          } else if (this.datePreset === "week") {
            const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return itemDate >= oneWeekAgo && itemDate <= now;
          } else if (this.datePreset === "month") {
            return (
              itemDate.getMonth() === now.getMonth() &&
              itemDate.getFullYear() === now.getFullYear()
            );
          } else if (this.datePreset === "custom") {
            if (this.startDate && this.endDate) {
              const start = new Date(this.startDate);
              start.setHours(0, 0, 0, 0);
              const end = new Date(this.endDate);
              end.setHours(23, 59, 59, 999);
              return itemDate >= start && itemDate <= end;
            } else if (this.startDate) {
              const start = new Date(this.startDate);
              start.setHours(0, 0, 0, 0);
              return itemDate >= start;
            } else if (this.endDate) {
              const end = new Date(this.endDate);
              end.setHours(23, 59, 59, 999);
              return itemDate <= end;
            }
          }
          return true;
        });
      }

      return list;
    },

    // Pagination calculations
    totalPages() {
      return Math.ceil(this.filteredDataList.length / this.itemsPerPage) || 1;
    },
    paginatedStart() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    paginatedEnd() {
      const end = this.paginatedStart + this.itemsPerPage;
      return end > this.filteredDataList.length ? this.filteredDataList.length : end;
    },
    paginatedItems() {
      return this.filteredDataList.slice(this.paginatedStart, this.paginatedEnd);
    },

    // Executive KPI Metrics
    kpiMetrics() {
      const list = this.filteredDataList;
      let totalBills = list.length;
      let totalItems = 0;
      let totalNetWeight = 0;
      let totalGrossWeight = 0;
      let totalStoneWeight = 0;
      let totalValuation = 0;
      let totalMeltWeight = 0;

      list.forEach((item) => {
        const net = Number((item._id && item._id.oldGoldNetWeight) || this.calculateTotalNet(item)) || 0;
        const gross = this.calculateTotalGross(item);
        const stone = this.calculateTotalStone(item);
        const amt = Number((item._id && item._id.oldGoldAmount) || this.calculateTotalAmount(item)) || 0;
        
        totalNetWeight += net;
        totalGrossWeight += gross;
        totalStoneWeight += stone;
        totalValuation += amt;

        if (item.items && Array.isArray(item.items)) {
          totalItems += item.items.length;
          item.items.forEach((i) => {
            const iNet = Number(i.netWeight || 0);
            const iMelt = Number(i.melt || 0);
            totalMeltWeight += (iNet * iMelt);
          });
        } else {
          totalItems += (item.count || 1);
        }
      });

      const avgRate = totalNetWeight > 0 ? (totalValuation / totalNetWeight) : 0;
      const avgMelt = totalNetWeight > 0 ? (totalMeltWeight / totalNetWeight) : 0;

      return {
        totalBills,
        totalItems,
        totalNetWeight,
        totalGrossWeight,
        totalStoneWeight,
        totalValuation,
        avgRate,
        avgMelt,
      };
    },
  },
  watch: {
    keyword() {
      this.currentPage = 1;
    },
    datePreset() {
      this.currentPage = 1;
    },
    startDate() {
      this.currentPage = 1;
    },
    endDate() {
      this.currentPage = 1;
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    getData() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/oldgold/getalllist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          keyword: this.keyword || undefined,
          page: 1,
          limit: 200,
          jewelleryId: this.juwellaryId,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.dataList = response.data.data || [];
          } else {
            this.showsnackbar = true;
            this.msg = response.data.msg || "Failed to load records";
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.error("Error loading old gold records:", err);
          this.showsnackbar = true;
          this.msg = "Server communication error";
        });
    },

    setDatePreset(preset) {
      this.datePreset = preset;
      this.startDate = null;
      this.endDate = null;
    },

    resetFilters() {
      this.keyword = "";
      this.datePreset = "all";
      this.startDate = null;
      this.endDate = null;
      this.currentPage = 1;
    },

    // Navigation to Bill Detail
    goToBill(item) {
      const salesId = item._id && (item._id._id || item._id);
      if (salesId) {
        this.$router.push(`/billDetail?id=${salesId}`);
      } else {
        this.showsnackbar = true;
        this.msg = "Sales bill ID not available";
      }
    },

    // Calculations per item / row
    calculateTotalGross(item) {
      if (item.items && Array.isArray(item.items) && item.items.length > 0) {
        return item.items.reduce((sum, i) => sum + (Number(i.grossWeight) || 0), 0);
      }
      return Number(item._id && item._id.oldGoldGrossWeight) || Number(item._id && item._id.oldGoldNetWeight) || 0;
    },

    calculateTotalStone(item) {
      if (item.items && Array.isArray(item.items) && item.items.length > 0) {
        return item.items.reduce((sum, i) => sum + (Number(i.stoneWeight) || 0), 0);
      }
      return 0;
    },

    calculateTotalNet(item) {
      if (item.items && Array.isArray(item.items) && item.items.length > 0) {
        return item.items.reduce((sum, i) => sum + (Number(i.netWeight) || 0), 0);
      }
      return Number(item._id && item._id.oldGoldNetWeight) || 0;
    },

    calculateTotalAmount(item) {
      if (item.items && Array.isArray(item.items) && item.items.length > 0) {
        return item.items.reduce((sum, i) => sum + (Number(i.itemAmount) || 0), 0);
      }
      return Number(item._id && item._id.oldGoldAmount) || 0;
    },

    calculatePureGold(net, melt) {
      const n = Number(net) || 0;
      const m = Number(melt) || 0;
      return (n * m) / 100;
    },

    calculateTotalPureGold(item) {
      if (item.items && Array.isArray(item.items)) {
        return item.items.reduce((sum, i) => sum + this.calculatePureGold(i.netWeight, i.melt), 0);
      }
      return 0;
    },

    calculateAvgMeltForItem(item) {
      if (!item.items || item.items.length === 0) return 0;
      const totalNet = this.calculateTotalNet(item);
      if (totalNet <= 0) return 0;
      const weightedMelt = item.items.reduce((sum, i) => sum + ((Number(i.netWeight) || 0) * (Number(i.melt) || 0)), 0);
      return weightedMelt / totalNet;
    },

    getItemIcon(name) {
      if (!name) return "mdi-circle-outline";
      const n = name.toLowerCase();
      if (n.includes("ring")) return "mdi-ring";
      if (n.includes("chain") || n.includes("necklace")) return "mdi-necklace";
      if (n.includes("bangle") || n.includes("bracelet")) return "mdi-circle-double";
      if (n.includes("earring") || n.includes("jhumka") || n.includes("stud")) return "mdi-rhombus-split-outline";
      if (n.includes("coin") || n.includes("bar") || n.includes("biscuit")) return "mdi-gold";
      return "mdi-cube-outline";
    },

    getInitials(name) {
      if (!name) return "W";
      const parts = name.trim().split(" ");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    },

    formatDate(dateStr) {
      if (!dateStr) return "-";
      const dt = new Date(dateStr);
      if (isNaN(dt.getTime())) return String(dateStr);
      const day = dt.getDate();
      const month = dt.toLocaleString("default", { month: "short" });
      const year = dt.getFullYear();
      return `${day} ${month} ${year}`;
    },

    formatTime(dateStr) {
      if (!dateStr) return "";
      const dt = new Date(dateStr);
      if (isNaN(dt.getTime())) return "";
      return dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
    },

    isToday(dateStr) {
      if (!dateStr) return false;
      const dt = new Date(dateStr);
      const now = new Date();
      return (
        dt.getDate() === now.getDate() &&
        dt.getMonth() === now.getMonth() &&
        dt.getFullYear() === now.getFullYear()
      );
    },

    formatCurrency(val) {
      const num = Number(val) || 0;
      return "₹ " + num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    copyText(text) {
      if (!text) return;
      navigator.clipboard.writeText(text);
      this.showsnackbar = true;
      this.msg = `Copied "${text}" to clipboard`;
    },

    openVoucherDialog(item) {
      this.selectedVoucher = item;
      this.voucherDialog = true;
    },

    printVoucherSlip() {
      const el = document.getElementById("printable-voucher");
      if (!el) return;
      const win = window.open("", "_blank", "width=700,height=750");
      win.document.write(`
        <html>
          <head>
            <title>Old Gold Intake Voucher</title>
            <style>
              body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 24px; color: #1e293b; font-size: 13px; }
              .store-name { font-size: 18px; font-weight: 800; text-align: center; color: #9a6b27; }
              .store-sub { font-size: 12px; text-align: center; color: #64748b; margin-bottom: 20px; }
              .voucher-meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 16px; border: 1px solid #e2e8f0; padding: 12px; border-radius: 6px; }
              .meta-label { font-size: 10px; font-weight: 700; color: #64748b; display: block; }
              .meta-val { font-size: 13px; font-weight: 600; color: #0f172a; }
              table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
              th, td { border: 1px solid #e2e8f0; padding: 8px; font-size: 12px; }
              th { background: #f8fafc; font-weight: 700; text-transform: uppercase; font-size: 10.5px; }
              .text-right { text-align: right; }
              .text-left { text-align: left; }
              .font-weight-bold { font-weight: 700; }
              .voucher-terms { font-size: 11px; color: #64748b; border: 1px dashed #cbd5e1; padding: 8px; border-radius: 4px; margin-bottom: 24px; }
              .sig-grid { display: flex; justify-content: space-between; margin-top: 40px; }
              .sig-line { width: 150px; border-top: 1px solid #0f172a; margin-bottom: 4px; }
            </style>
          </head>
          <body>
            ${el.innerHTML}
          </body>
        </html>
      `);
      win.document.close();
      win.focus();
      setTimeout(() => {
        win.print();
        win.close();
      }, 350);
    },

    printSummaryReport() {
      window.print();
    },

    // Export complete table + items to CSV
    exportToCSV() {
      const items = this.filteredDataList;
      if (!items || items.length === 0) return;

      const headers = [
        "Bill No",
        "Date",
        "Customer Name",
        "Contact",
        "Item S.No",
        "Item Name",
        "Metal",
        "Purity (K)",
        "Gold Rate (INR/g)",
        "Gross Wt (g)",
        "Stone Wt (g)",
        "Net Wt (g)",
        "Melt %",
        "Pure Gold Wt (g)",
        "Item Amount (INR)",
        "Bill Total Amount (INR)"
      ];

      const rows = [];

      items.forEach((record) => {
        const billNo = (record._id && record._id.billNo) || "N/A";
        const date = this.formatDate(record._id && record._id.create_date);
        const customer = (record._id && record._id.customerName) || "Walk-in";
        const phone = (record._id && record._id.mobileNumber) || "";
        const billTotal = (record._id && record._id.oldGoldAmount) || this.calculateTotalAmount(record);

        if (record.items && record.items.length > 0) {
          record.items.forEach((it, idx) => {
            const pureGold = this.calculatePureGold(it.netWeight, it.melt);
            rows.push([
              `"${billNo}"`,
              `"${date}"`,
              `"${customer}"`,
              `"${phone}"`,
              idx + 1,
              `"${it.itemName || 'Old Gold'}"`,
              `"${it.metalType || 'Gold'}"`,
              `"${it.purity || ''}"`,
              it.goldRate || 0,
              Number(it.grossWeight || 0).toFixed(3),
              Number(it.stoneWeight || 0).toFixed(3),
              Number(it.netWeight || 0).toFixed(3),
              Number(it.melt || 0).toFixed(1),
              pureGold.toFixed(3),
              Number(it.itemAmount || 0).toFixed(2),
              Number(billTotal || 0).toFixed(2),
            ]);
          });
        } else {
          rows.push([
            `"${billNo}"`,
            `"${date}"`,
            `"${customer}"`,
            `"${phone}"`,
            1,
            `"Old Gold"`,
            `"Gold"`,
            `""`,
            0,
            this.calculateTotalGross(record).toFixed(3),
            0,
            this.calculateTotalNet(record).toFixed(3),
            0,
            0,
            Number(billTotal || 0).toFixed(2),
            Number(billTotal || 0).toFixed(2),
          ]);
        }
      });

      const csvContent =
        "data:text/csv;charset=utf-8," +
        [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `Old_Gold_Inward_Register_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped>
.oldgold-page-wrapper {
  padding: 8px 12px;
  background-color: transparent;
}

/* Executive Header Card */
.header-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
}

.header-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: linear-gradient(135deg, #fef9c3 0%, #fef08a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #fde047;
}

.page-title {
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: #0f172a;
}

.page-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.badge-gold-count {
  font-size: 11px;
  font-weight: 700;
  background: #fefce8;
  color: #b45309;
  border: 1px solid #fef08a;
  border-radius: 6px;
  padding: 2px 8px;
}

.refresh-icon-btn {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 8px;
}

.btn-secondary {
  border: 1px solid #cbd5e1 !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  height: 36px !important;
}

.btn-gold-outline {
  border: 1px solid #9a6b27 !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  height: 36px !important;
}

.btn-primary {
  font-size: 12px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  height: 36px !important;
}

/* 4 KPI Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
}

.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
  transition: all 0.2s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin: 4px 0 2px;
}

.kpi-value.gold-val {
  color: #9a6b27;
}

.unit-text {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
}

.kpi-hint {
  font-size: 11.5px;
  color: #64748b;
  display: flex;
  align-items: center;
}

.kpi-icon-pill {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Filter Card */
.filter-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02) !important;
}

.search-input-wrap {
  flex: 1;
  min-width: 260px;
  max-width: 380px;
}

.custom-search-field :deep(.v-input__control) {
  border-radius: 8px !important;
}

.custom-search-field :deep(fieldset) {
  border-color: #cbd5e1 !important;
}

.custom-search-field :deep(input) {
  font-size: 13px !important;
}

.preset-btn {
  font-size: 11.5px !important;
  font-weight: 600 !important;
  text-transform: capitalize !important;
  border-radius: 6px !important;
  border: 1px solid #e2e8f0 !important;
  background: #f8fafc !important;
  color: #475569 !important;
}

.preset-active {
  background: #9a6b27 !important;
  color: #ffffff !important;
  border-color: #9a6b27 !important;
}

.date-input-field {
  width: 140px;
}

.date-input-field :deep(input) {
  font-size: 12px !important;
}

/* Table Card */
.table-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
}

.table-header-bar {
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.table-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table-count-badge {
  font-size: 11px;
  font-weight: 700;
  background: #e2e8f0;
  color: #334155;
  padding: 1px 6px;
  border-radius: 4px;
}

.rows-select {
  width: 75px;
}

.rows-select :deep(input) {
  font-size: 12px !important;
}

/* Data Table Styling */
.pro-oldgold-table :deep(th) {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 12px 14px !important;
  border-bottom: 1px solid #e2e8f0 !important;
  white-space: nowrap;
}

.pro-oldgold-table :deep(td) {
  font-size: 13px !important;
  color: #334155 !important;
  padding: 10px 14px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.pro-oldgold-table :deep(tr:hover) {
  background-color: #fafbfc !important;
}

/* Bill Badge */
.bill-badge {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  background: #eff6ff;
  color: #1d4ed8;
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid #bfdbfe;
  cursor: pointer;
  transition: all 0.15s ease;
}

.bill-badge:hover {
  background: #1d4ed8;
  color: #ffffff;
}

/* Date cell */
.date-cell {
  line-height: 1.3;
}

.date-text {
  font-size: 12.5px;
  font-weight: 600;
  color: #1e293b;
}

.date-sub {
  font-size: 11px;
  color: #94a3b8;
}

.tag-today {
  font-size: 9.5px;
  font-weight: 700;
  background: #dcfce7;
  color: #15803d;
  padding: 1px 5px;
  border-radius: 4px;
}

/* Customer cell */
.customer-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #334155;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #cbd5e1;
}

.customer-name {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  text-transform: capitalize;
}

.customer-phone {
  font-size: 11.5px;
  color: #64748b;
  display: flex;
  align-items: center;
}

.copy-btn {
  width: 18px;
  height: 18px;
}

/* Items badge */
.items-count-badge {
  font-size: 11px;
  font-weight: 600;
  background: #fdf4ff;
  color: #9333ea;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid #f0abfc;
  display: inline-flex;
  align-items: center;
}

/* Weight Badge */
.weight-badge {
  font-size: 12.5px;
  font-weight: 700;
  background: #fefce8;
  color: #92400e;
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid #fef08a;
  display: inline-flex;
  align-items: center;
}

.unit-sub {
  font-size: 11px;
  color: #94a3b8;
}

/* Amount cell */
.amount-cell {
  text-align: left;
}

.amount-val {
  font-size: 13.5px;
  font-weight: 800;
  color: #047857;
}

.table-action-btn {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 6px;
}

.table-action-btn:hover {
  background: #f1f5f9;
}

/* Expanded Item Breakdown */
.expanded-row-cell {
  background: #fbfcfe;
}

.item-breakdown-wrapper {
  background: #ffffff;
  border-top: 2px solid #9a6b27;
  border-bottom: 1px solid #e2e8f0;
  margin: 6px 12px 12px;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}

.sub-icon-box {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: #fefce8;
  display: flex;
  align-items: center;
  justify-content: center;
}

.breakdown-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
}

.sub-bill-badge {
  font-size: 11px;
  font-weight: 700;
  background: #f1f5f9;
  color: #475569;
  padding: 1px 6px;
  border-radius: 4px;
}

.btn-dark {
  font-size: 11px !important;
  font-weight: 600 !important;
  border-radius: 6px !important;
  height: 28px !important;
}

/* Nested Table */
.nested-table-wrap {
  overflow-x: auto;
}

.nested-table {
  width: 100%;
  border-collapse: collapse;
}

.nested-table th {
  background: #f8fafc;
  color: #475569;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 8px 10px;
  border-bottom: 1px solid #e2e8f0;
}

.nested-table td {
  font-size: 12px;
  color: #334155;
  padding: 8px 10px;
  border-bottom: 1px solid #f1f5f9;
}

.nested-table-row:hover {
  background: #f8fafc;
}

.item-name-text {
  font-weight: 600;
  color: #0f172a;
}

.purity-badge {
  font-size: 10px;
  font-weight: 700;
  background: #f1f5f9;
  color: #475569;
  padding: 1px 5px;
  border-radius: 4px;
}

.rate-text {
  font-size: 11.5px;
  color: #475569;
}

.melt-badge {
  font-size: 10.5px;
  font-weight: 700;
  background: #fef3c7;
  color: #92400e;
  padding: 1px 5px;
  border-radius: 4px;
}

.nested-table-footer td {
  background: #f8fafc;
  border-top: 1px solid #cbd5e1;
  border-bottom: none;
  font-size: 12px;
}

/* Pagination Bar */
.table-pagination-bar {
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
}

.pagination-info {
  font-size: 12.5px;
  color: #64748b;
}

.custom-pagination :deep(.v-pagination__item) {
  font-size: 12px !important;
  min-width: 28px !important;
  height: 28px !important;
  border-radius: 6px !important;
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
}

.custom-pagination :deep(.v-pagination__navigation) {
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 6px !important;
  width: 28px !important;
  height: 28px !important;
}

/* Voucher Dialog Modal */
.voucher-modal-card {
  background: #ffffff;
}

.voucher-modal-title {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: 0.5px;
}

.voucher-modal-sub {
  font-size: 11px;
  color: #64748b;
}

.voucher-body {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fafbfc;
}

.store-name {
  font-size: 16px;
  font-weight: 800;
  color: #9a6b27;
  letter-spacing: 0.5px;
}

.store-sub {
  font-size: 11.5px;
  color: #64748b;
}

.voucher-meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 10px;
}

.meta-label {
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  display: block;
}

.meta-val {
  font-size: 12.5px;
  font-weight: 700;
  color: #0f172a;
}

.voucher-table {
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
}

.voucher-table th {
  background: #f1f5f9;
  color: #475569;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 6px 8px;
  border-bottom: 1px solid #e2e8f0;
}

.voucher-table td {
  padding: 6px 8px;
  font-size: 11.5px;
  border-bottom: 1px solid #f1f5f9;
}

.voucher-terms {
  font-size: 10.5px;
  color: #64748b;
  border: 1px dashed #cbd5e1;
  background: #ffffff;
  border-radius: 4px;
}

.sig-line {
  width: 130px;
  border-top: 1px solid #0f172a;
}

/* Empty State */
.empty-state-wrap {
  padding: 40px 20px;
}

.empty-icon-circle {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.empty-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

/* Utilities */
.border-b {
  border-bottom: 1px solid #e2e8f0;
}

.border-t {
  border-top: 1px solid #e2e8f0;
}

.text-emerald {
  color: #059669;
}

.text-gold {
  color: #9a6b27;
}

.text-muted {
  color: #94a3b8;
}

.text-slate-700 {
  color: #334155;
}

.text-slate-800 {
  color: #1e293b;
}

.text-slate-900 {
  color: #0f172a;
}
</style>
